import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle, X, Mail, AlertCircle, Loader2, Zap, AlertTriangle, Sparkles } from 'lucide-react';
export type ToastType = 'success' | 'error' | 'info' | 'pending' | 'email-success' | 'email-error' | 'scan-complete' | 'scan-failed' | 'rewrite-success';
interface ToastProps {
  message: string;
  isVisible: boolean;
  onClose: () => void;
  duration?: number;
  type?: ToastType;
  actionLabel?: string;
  onAction?: () => void;
}
export function Toast({
  message,
  isVisible,
  onClose,
  duration = 3000,
  type = 'success',
  actionLabel,
  onAction
}: ToastProps) {
  const [progress, setProgress] = useState(100);
  useEffect(() => {
    if (!isVisible || duration <= 0 || type === 'pending') {
      setProgress(100);
      return;
    }
    // Auto-dismiss timer
    const timer = setTimeout(() => {
      onClose();
    }, duration);
    // Progress bar animation
    setProgress(100);
    const startTime = Date.now();
    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, 100 - elapsed / duration * 100);
      setProgress(remaining);
      if (remaining <= 0) clearInterval(interval);
    }, 30);
    return () => {
      clearTimeout(timer);
      clearInterval(interval);
    };
  }, [isVisible, duration, onClose, type]);
  const getStyles = () => {
    switch (type) {
      case 'success':
        return {
          border: 'border-emerald-500/30',
          iconBg: 'bg-emerald-500/15',
          iconColor: 'text-emerald-400',
          progressColor: 'bg-emerald-500',
          Icon: CheckCircle
        };
      case 'scan-complete':
        return {
          border: 'border-emerald-500/30',
          iconBg: 'bg-emerald-500/15',
          iconColor: 'text-emerald-400',
          progressColor: 'bg-emerald-500',
          Icon: Zap
        };
      case 'scan-failed':
        return {
          border: 'border-red-500/30',
          iconBg: 'bg-red-500/15',
          iconColor: 'text-red-400',
          progressColor: 'bg-red-500',
          Icon: AlertTriangle
        };
      case 'rewrite-success':
        return {
          border: 'border-amber-500/30',
          iconBg: 'bg-amber-500/15',
          iconColor: 'text-amber-400',
          progressColor: 'bg-amber-500',
          Icon: Sparkles
        };
      case 'email-success':
        return {
          border: 'border-emerald-500/30',
          iconBg: 'bg-emerald-500/15',
          iconColor: 'text-emerald-400',
          progressColor: 'bg-emerald-500',
          Icon: Mail
        };
      case 'error':
        return {
          border: 'border-red-500/30',
          iconBg: 'bg-red-500/15',
          iconColor: 'text-red-400',
          progressColor: 'bg-red-500',
          Icon: AlertCircle
        };
      case 'email-error':
        return {
          border: 'border-red-500/30',
          iconBg: 'bg-red-500/15',
          iconColor: 'text-red-400',
          progressColor: 'bg-red-500',
          Icon: Mail
        };
      case 'pending':
        return {
          border: 'border-blue-500/30',
          iconBg: 'bg-blue-500/15',
          iconColor: 'text-blue-400',
          progressColor: 'bg-blue-500',
          Icon: Loader2,
          spin: true
        };
      case 'info':
        return {
          border: 'border-blue-500/30',
          iconBg: 'bg-blue-500/15',
          iconColor: 'text-blue-400',
          progressColor: 'bg-blue-500',
          Icon: AlertCircle
        };
      default:
        return {
          border: 'border-gray-700',
          iconBg: 'bg-gray-800',
          iconColor: 'text-gray-400',
          progressColor: 'bg-gray-500',
          Icon: CheckCircle
        };
    }
  };
  const styles = getStyles();
  const Icon = styles.Icon;
  const showProgress = duration > 0 && type !== 'pending';
  const isErrorType = type === 'error' || type === 'email-error' || type === 'scan-failed';
  return <AnimatePresence data-id="element-2073">
      {isVisible && <motion.div initial={{
      opacity: 0,
      y: -40,
      scale: 0.97
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1
    }} exit={{
      opacity: 0,
      y: -15,
      scale: 0.97
    }} transition={{
      type: 'spring',
      stiffness: 400,
      damping: 30
    }} className="fixed top-5 left-1/2 -translate-x-1/2 z-[9999] pointer-events-auto w-full max-w-sm px-4 sm:px-0" role={isErrorType ? 'alert' : 'status'} aria-live={isErrorType ? 'assertive' : 'polite'} aria-atomic="true" data-id="element-2074">
          <div className={`
              bg-[#0a0a0a] ${styles.border}
              border rounded-xl
              shadow-[0_8px_30px_rgba(0,0,0,0.5)]
              overflow-hidden
              backdrop-blur-md
            `} data-id="element-2075">
            <div className="p-4 flex items-start gap-3" data-id="element-2076">
              <div className={`${styles.iconBg} rounded-lg p-2 flex-shrink-0`} data-id="element-2077">
                <Icon size={16} className={`${styles.iconColor} ${(styles as any).spin ? 'animate-spin' : ''}`} strokeWidth={2.5} data-id="element-2078" />
              </div>

              <div className="flex-1 min-w-0 pt-0.5" data-id="element-2079">
                <p className="text-white font-mono text-sm leading-snug" data-id="element-2080">
                  {message}
                </p>

                {actionLabel && onAction && <button onClick={onAction} className="mt-2 text-xs font-bold text-gray-400 hover:text-white uppercase tracking-wider transition-colors" data-id="element-2081">
                    {actionLabel}
                  </button>}
              </div>

              <button onClick={onClose} className="p-1 -mr-1 text-gray-600 hover:text-white hover:bg-white/5 rounded-lg transition-colors flex-shrink-0" aria-label="Close notification" data-id="element-2082">
                <X size={14} data-id="element-2083" />
              </button>
            </div>

            {/* Progress bar */}
            {showProgress && <div className="h-[2px] w-full bg-gray-800" data-id="element-2084">
                <div className={`h-full ${styles.progressColor} transition-none`} style={{
            width: `${progress}%`
          }} data-id="element-2085" />
              </div>}
          </div>
        </motion.div>}
    </AnimatePresence>;
}